import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { PricingContextState, PricingTier, NationalityType } from '../types';
import { apiService } from '../services/apiService';
import { countries, getCountryByCode } from '../utils/countries';

interface PricingContextValue extends PricingContextState {
    setNationality: (type: NationalityType) => void;
    setCountry: (code: string) => void;
    setTier: (tier: PricingTier) => void;
    setDates: (start: string, end: string) => void;
    setPax: (adults: number, children: number) => void;
    setCurrency: (currency: string) => void;
    setSearchQuery: (query: string) => void;
    setDuration: (duration: string) => void;
    setDifficulty: (difficulty: string) => void;
    setBudgetRange: (min: number, max: number) => void;
    setCategory: (category: string) => void;
    setPaymentPlan: (plan: 'full' | 'deposit' | 'installments') => void;
    setIsFilterOpen: (isOpen: boolean) => void;
    resetFilters: () => void;
    // Dynamic data
    availableCategories: string[];
    exchangeRates: { [key: string]: number };
}

const PricingContext = createContext<PricingContextValue | undefined>(undefined);

export const PricingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [availableCategories, setAvailableCategories] = useState<string[]>(['All']);
    const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});

    // Default travel dates: 7 days from now for 3 days
    const getDefaultTravelDates = () => {
        const startDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const endDate = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        return { startDate, endDate };
    };

    // Initialize state with default values
    const [state, setState] = useState<PricingContextState>(() => {
        const defaultState: PricingContextState = {
            nationalityType: NationalityType.INTERNATIONAL,
            countryCode: 'US',
            displayCurrency: 'USD',
            tier: PricingTier.STANDARD,
            travelDates: getDefaultTravelDates(),
            pax: { adults: 1, children: 0 },
            searchQuery: '',
            duration: 'Any Duration',
            difficulty: 'Any Level',
            budgetRange: { min: 0, max: 10000 },
            category: 'All',
            isFilterOpen: false,
            paymentPlan: 'full'
        };

        try {
            const saved = localStorage.getItem('pricing_context');
            if (saved) {
                const parsed = JSON.parse(saved);

                // Migration: Clear old context if travelDates is null
                if (parsed.travelDates === null || parsed.travelDates === undefined) {
                    // Handle backward compatibility

                    localStorage.removeItem('pricing_context');
                    return defaultState;
                }

                // Ensure category is never undefined
                if (!parsed.category) parsed.category = 'All';
                // Merge saved state with defaults, ensuring travelDates are set
                const travelDates = parsed.travelDates || defaultState.travelDates;
                
                // Truncate ISO strings if present for HTML5 date inputs
                if (travelDates.startDate?.includes('T')) travelDates.startDate = travelDates.startDate.split('T')[0];
                if (travelDates.endDate?.includes('T')) travelDates.endDate = travelDates.endDate.split('T')[0];

                return {
                    ...defaultState,
                    ...parsed,
                    travelDates,
                    budgetRange: parsed.budgetRange || defaultState.budgetRange
                };
            }
        } catch (e) {
            console.error('Error loading pricing context:', e);
            // Clear corrupted cache
            localStorage.removeItem('pricing_context');
        }

        return defaultState;
    });

    // Fetch dynamic data: Categories and Exchange Rates
    useEffect(() => {
        const fetchMetadata = async () => {
            try {
                const CACHE_DURATION = 60 * 60 * 1000; // 1 hour
                const now = Date.now();

                // 1. Fetch Categories (with caching)
                const cachedCategories = sessionStorage.getItem('categories_metadata');
                let categoriesWasCached = false;

                if (cachedCategories) {
                    try {
                        const { timestamp, data } = JSON.parse(cachedCategories);
                        if (now - timestamp < CACHE_DURATION && data && data.length > 0) {
                            const catNames = ['All', ...data.map((c: any) => c.name)];
                            setAvailableCategories(catNames);
                            categoriesWasCached = true;
                        }
                    } catch (e) {
                        sessionStorage.removeItem('categories_metadata');
                    }
                }

                if (!categoriesWasCached) {
                    const categoriesData = await apiService.getAllCategories(true);
                    if (categoriesData && categoriesData.length > 0) {
                        const catNames = ['All', ...categoriesData.map(c => c.name)];
                        setAvailableCategories(catNames);
                        sessionStorage.setItem('categories_metadata', JSON.stringify({ timestamp: now, data: categoriesData }));
                    }
                }

                // 2. Fetch Exchange Rates with Caching & Fallback
                const cachedRates = sessionStorage.getItem('exchangeRates');
                let rates: Record<string, number> | null = null;
                let ratesWasCached = false;

                if (cachedRates) {
                    try {
                        const { timestamp, rates: data } = JSON.parse(cachedRates);
                        if (now - timestamp < CACHE_DURATION && data && Object.keys(data).length > 0) {
                            rates = data;
                            ratesWasCached = true;
                        }
                    } catch (e) {
                        sessionStorage.removeItem('exchangeRates');
                    }
                }

                if (!ratesWasCached) {
                    try {
                        rates = await apiService.getExchangeRates();
                    } catch (e) {
                        console.warn('⚠️ API rates call failed, will try fallback');
                    }

                    // Validate if backend returned valid rates
                    if (!rates || Object.keys(rates).length === 0) {
                        console.warn('⚠️ Backend rates unavailable. Trying FawazAhmed0 API fallback...');
                        try {
                            const fallbackResponse = await fetch('https://latest.currency-api.pages.dev/v1/currencies/usd.json');
                            if (fallbackResponse.ok) {
                                const data = await fallbackResponse.json();
                                const rawRates = data.usd || {};
                                rates = {};
                                Object.keys(rawRates).forEach(key => {
                                    rates![key.toUpperCase()] = rawRates[key];
                                });
                            }
                        } catch (fallbackError) {
                            console.error('❌ FawazAhmed0 API fallback failed:', fallbackError);
                        }
                    }
                }

                if (rates && Object.keys(rates).length > 0) {
                    setExchangeRates(rates);
                    if (!ratesWasCached) {
                        sessionStorage.setItem('exchangeRates', JSON.stringify({ timestamp: now, rates }));
                    }
                }
            } catch (error) {
                console.error('Error fetching pricing metadata:', error);
            }
        };

        fetchMetadata();
    }, []);

    useEffect(() => {
        localStorage.setItem('pricing_context', JSON.stringify(state));
        window.dispatchEvent(new CustomEvent('pricingContextChanged', { detail: state }));
    }, [state]);

    const setNationality = (nationalityType: NationalityType) => {
        setState(prev => ({
            ...prev,
            nationalityType
        }));
    };

    const setCountry = (countryCode: string) => {
        const country = getCountryByCode(countryCode);
        const nationalityType = countryCode === 'KE' ? NationalityType.LOCAL : NationalityType.INTERNATIONAL;

        // Auto-select currency based on country:
        // 1. If Local (KE) -> KES
        // 2. If International -> Use country's local currency if available in rates, else USD
        let newCurrency = 'USD';
        if (nationalityType === NationalityType.LOCAL) {
            newCurrency = 'KES';
        } else if (country && country.currency) {
            const currencyCode = country.currency.toUpperCase();
            // Only switch if we have a rate for it, otherwise stay in USD (safest global default)
            if (exchangeRates && exchangeRates[currencyCode]) {
                newCurrency = currencyCode;
            } else {
                newCurrency = 'USD';
            }
        }

        setState(prev => ({
            ...prev,
            countryCode,
            nationalityType,
            displayCurrency: newCurrency
        }));

        // Dispatch event for immediate updates across the app
        window.dispatchEvent(new CustomEvent('pricingContextChanged', {
            detail: { countryCode, nationalityType, currency: newCurrency }
        }));
    };

    const setTier = (tier: PricingTier) => {
        setState(prev => ({ ...prev, tier }));
        // Dispatch event for immediate updates
        window.dispatchEvent(new CustomEvent('pricingContextChanged', { detail: { tier } }));
    };

    const setDates = (startDate: string, endDate: string) => {
        // Ensure format is yyyy-MM-dd for HTML5 date inputs
        const format = (d: string) => d?.includes('T') ? d.split('T')[0] : d;
        setState(prev => ({ 
            ...prev, 
            travelDates: { 
                startDate: format(startDate), 
                endDate: format(endDate) 
            } 
        }));
    };

    const setPax = (adults: number, children: number) => {
        setState(prev => ({ ...prev, pax: { adults, children } }));
    };

    const setCurrency = (displayCurrency: string) => {
        setState(prev => ({ ...prev, displayCurrency }));
    };

    const setSearchQuery = (searchQuery: string) => setState(prev => ({ ...prev, searchQuery }));
    const setDuration = (duration: string) => setState(prev => ({ ...prev, duration }));
    const setDifficulty = (difficulty: string) => setState(prev => ({ ...prev, difficulty }));
    const setBudgetRange = (min: number, max: number) => setState(prev => ({ ...prev, budgetRange: { min, max } }));
    const setCategory = (category: string) => setState(prev => ({ ...prev, category: category || 'All' }));
    const setIsFilterOpen = (isOpen: boolean) => setState(prev => ({ ...prev, isFilterOpen: isOpen }));

    const setPaymentPlan = (paymentPlan: 'full' | 'deposit' | 'installments') => {
        setState(prev => ({ ...prev, paymentPlan }));
    };

    const resetFilters = useCallback(() => {
        setState(prev => ({
            ...prev,
            searchQuery: '',
            category: 'All',
            duration: 'Any Duration',
            difficulty: 'Any Level',
            budgetRange: { min: 0, max: 10000 },
            isFilterOpen: false
        }));
    }, []);

    const contextValue = useMemo<PricingContextValue>(() => ({
        ...state,
        setNationality,
        setCountry,
        setTier,
        setDates,
        setPax,
        setCurrency,
        setSearchQuery,
        setDuration,
        setDifficulty,
        setBudgetRange,
        setCategory,
        setPaymentPlan,
        setIsFilterOpen,
        resetFilters,
        availableCategories,
        exchangeRates
    }), [state, availableCategories, exchangeRates]);

    return (
        <PricingContext.Provider value={contextValue}>
            {children}
        </PricingContext.Provider>
    );
};

export const usePricing = () => {
    const context = useContext(PricingContext);
    if (context === undefined) {
        throw new Error('usePricing must be used within a PricingProvider');
    }
    return context;
};
